/**
 * The persistent connection indicator (PROJECT_SPEC.md §10; OFF-001, OFF-011): says whether the server can be reached,
 * how many captures exist only on this device, and how long offline capture is still allowed. "Pending" is worded and
 * drawn as pending, never as recorded.
 *
 * Two parts (design-system/MASTER.md, Navigation): a small ConnectionChip that is ALWAYS in the top bar (zero queued
 * included: "nothing queued" is itself information, URS-10.5-006), and the OfflineBanner band, which exists ONLY
 * when something needs attention: the device is offline, entries are waiting or refused, or the engine needs a sign-in.
 */
import { Link } from "wouter";
import { useOffline } from "./OfflineProvider.jsx";
import { cx, Icon } from "./ui/index.js";

export function formatRemaining(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  return minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`;
}

function useConnection() {
  const { state, online } = useOffline();
  const unsent = state?.queue.filter((c) => c.state === "QUEUED" || c.state === "SENT").length ?? 0;
  const refused = state?.queue.filter((c) => c.state === "REJECTED").length ?? 0;
  const exhausted = !online && state?.remainingMs === 0;
  const engineNotice =
    state?.phase === "NEEDS_SIGN_IN" || state?.phase === "WRONG_USER" || state?.phase === "FAILED"
      ? state.notice
      : null;
  const attention = !online || unsent > 0 || refused > 0 || !!engineNotice;
  return { state, online, unsent, refused, exhausted, engineNotice, attention };
}

/** The small always-on indicator for the top bar. Not a control: the band and the rail's "Offline queue" entry are the way in. */
export function ConnectionChip() {
  const { online, unsent, refused } = useConnection();
  const tone = refused > 0 ? "text-alarm" : online ? "text-ok" : "text-warn";
  return (
    <span
      role="status"
      aria-live="polite"
      data-testid="connection-chip"
      data-online={online}
      className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-line bg-surface px-3 text-xs font-medium text-ink"
    >
      <Icon name={online ? "cloud_done" : "cloud_off"} size={16} className={tone} />
      <span>{online ? "Online" : "Offline"}</span>
      <span aria-hidden="true" className="text-ink-muted">
        ·
      </span>
      <span
        data-testid="queued-count"
        data-count={unsent}
        className={cx(unsent > 0 && "font-semibold text-warn")}
      >
        {unsent > 0 ? `${unsent} pending sync` : "0 queued"}
      </span>
    </span>
  );
}

/** The band: rendered only when there is something to act on. */
export function OfflineBanner() {
  const { state, online, unsent, refused, exhausted, engineNotice, attention } = useConnection();
  if (!attention) return null;
  const alarm = exhausted || refused > 0;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      data-online={online}
      className={cx(
        "sticky top-0 z-40 flex flex-wrap items-center gap-3 border-b px-6 text-sm font-medium text-ink",
        alarm ? "border-line bg-alarm-subtle" : "border-line bg-warn-subtle",
      )}
    >
      <Icon
        name={online ? "cloud_done" : "cloud_off"}
        size={20}
        className={alarm ? "text-alarm" : "text-warn"}
      />
      <span>
        {online
          ? "Online — entries are waiting to sync."
          : exhausted
            ? "Offline — the offline limit has been reached. Connect and sync before recording anything further."
            : "Offline — what you record is kept on this device and is not in the batch record until it syncs."}
      </span>
      {/* ink text on the band: floor banners are AAA (URS-10.5-001), which the coloured chips do not reach on a tint */}
      {unsent > 0 ? (
        <span className="inline-flex items-center gap-1 font-semibold">
          <Icon name="schedule" size={16} className="text-warn" />
          {`${unsent} pending sync`}
        </span>
      ) : null}
      {refused > 0 ? (
        <span className="inline-flex items-center gap-1 font-semibold">
          <Icon name="error" size={16} className="text-alarm" />
          {`${refused} refused — needs your attention`}
        </span>
      ) : null}
      {!online && state?.remainingMs != null && state.remainingMs > 0 ? (
        <span>Offline capture allowed for {formatRemaining(state.remainingMs)} more</span>
      ) : null}
      {engineNotice ? <span>{engineNotice}</span> : null}
      <Link
        href="/offline"
        className="ml-auto inline-flex min-h-[var(--touch-floor)] items-center px-3 font-semibold text-brand underline"
      >
        Offline queue
      </Link>
    </div>
  );
}
